<!--
  ClientsList.vue -- Super Admin cross-distributor Clients list.

  Card: SA cross-distributor Clients list -- https://trello.com/c/fyVy1dZF
  Pebbles:
    * Frontend SA Clients list page reusing distributor client
      components + distributor/store filters.
    * Sidebar entry + dashboard-summary count.

  The cross-tenant twin of the Distributor Clients workspace
  (``ClientsListPage.vue``). Where the distributor surface is scoped to
  ONE tenant, this page lists clients ACROSS every distributor by
  fetching the SA-gated ``GET /api/clients/superadmin-summary`` endpoint
  (merged PR #1781). Each row carries the standard per-client rollup
  fields PLUS ``distributor_id`` + ``distributor_name``, so we surface
  the owning distributor as a column on every row.

  Results surface: a hand-rolled sortable <table> using the SA
  sortable-table idiom (columns Name / Distributor / Email /
  Status / Last activity). Same reasoning as the distributor list: a
  plain semantic <table> stays testable without a configured Vuetify
  instance in vitest, and the columns are short-lived. Columns are
  sortable (name asc default; toggle direction on the second click of the
  same column; null sorts low). Clicking a row opens that client's
  detail page. The per-row "Impersonate" action (far right of the actions
  cell) impersonates the client -- "view as" it on the Client Portal,
  parallel to the SA Distributors "Impersonate" action (card
  https://trello.com/c/8eq8uc8E).

  Reuse:
    * ``ClientsFilterChips.vue`` drives the client-side attention chips
      (Issues / Onboarding / Active / Inactive / Quiet / All) over the
      fetched page.

  Server-side filters (distributor dropdown + search + optional store
  filter) map to the endpoint's ``distributor_id`` / ``search`` /
  ``store_id`` query params and re-fetch on change. The page follows the
  ``DistributorList.vue`` fetch pattern: ``$axios`` + a ``buildParams``
  helper + ``TablePagination`` + debounced search.

  Selector convention: data-testid on every interactive element.
-->
<template>
    <v-container fluid class="main-container" data-testid="sa-clients-page">
        <div class="sacl-page">
            <header class="sacl-page__header">
                <h2 class="sacl-page__title">Clients</h2>
                <p class="sacl-page__subtitle">
                    Every client across all distributors.
                </p>
            </header>

            <div class="sacl-toolbar">
                <ClientsFilterChips
                    :filter="chipFilter"
                    :counts="filterCounts"
                    data-testid="sa-clients-filter-chips"
                    @change="onChipChange"
                />
                <div class="sacl-toolbar__server-filters">
                    <select
                        v-model="distributorFilter"
                        class="sacl-select"
                        data-testid="sa-clients-distributor-filter"
                        @change="onServerFilterChange"
                    >
                        <option :value="null">All distributors</option>
                        <option
                            v-for="d in distributorOptions"
                            :key="d.id"
                            :value="d.id"
                        >
                            {{ d.name }}
                        </option>
                    </select>
                    <input
                        v-model="searchInput"
                        type="search"
                        class="sacl-search"
                        placeholder="Search clients by name, email, or phone"
                        data-testid="sa-clients-search"
                        @input="onSearchInput"
                    />
                </div>
            </div>

            <div
                v-if="loading"
                class="sacl-state sacl-state--loading"
                data-testid="sa-clients-loading"
            >
                <v-progress-circular indeterminate color="primary" />
                <span class="sacl-state__text">Loading clients...</span>
            </div>

            <div
                v-else-if="error"
                class="sacl-state sacl-state--error"
                data-testid="sa-clients-error"
            >
                {{ error }}
            </div>

            <div
                v-else-if="clients.length === 0"
                class="sacl-state sacl-state--empty"
                data-testid="sa-clients-empty"
            >
                No clients match the current filters.
            </div>

            <div
                v-else-if="visibleClients.length === 0"
                class="sacl-state sacl-state--empty"
                data-testid="sa-clients-no-matches"
            >
                No clients match the selected chip. Try a different chip
                or clear the search.
            </div>

            <table v-else class="sa-table" data-testid="sa-clients-table">
                <thead>
                    <tr>
                        <th
                            v-for="col in columns"
                            :key="col.key"
                            :data-testid="`sa-col-${col.key}`"
                            class="sa-table__header"
                            @click="toggleSort(col.key)"
                        >
                            {{ col.label }}
                            <span
                                v-if="sortKey === col.key"
                                class="sa-table__sort-indicator"
                                data-testid="sort-indicator"
                                >{{ sortDir === "asc" ? "↑" : "↓" }}</span
                            >
                        </th>
                        <!-- Actions column is not sortable. -->
                        <th
                            class="sa-table__header sa-table__header--actions"
                            data-testid="sa-col-actions"
                        >
                            Actions
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="row in sortedClients"
                        :key="row.id"
                        :data-row-id="row.id"
                        :data-testid="`sa-clients-row-${row.id}`"
                        class="sa-table__row sa-table__row--link"
                        :class="{
                            'sa-table__row--inactive':
                                row.status === 'inactive',
                        }"
                        tabindex="0"
                        :aria-label="`Open ${row.name || 'client'} details`"
                        @click="openClient(row)"
                        @keydown.enter.self="openClient(row)"
                    >
                        <td>{{ row.name || "--" }}</td>
                        <td>
                            <DistributorLink
                                :distributor-id="row.distributor_id"
                                :name="row.distributor_name"
                                fallback="Unknown distributor"
                            />
                        </td>
                        <td>{{ row.email || "--" }}</td>
                        <td>
                            <span
                                class="sacl-invitation-chip"
                                :data-testid="`sa-clients-invitation-chip-${row.id}`"
                            >
                                {{ invitationStatusLabel(row.invitation_status) }}
                            </span>
                        </td>
                        <td>
                            <span
                                class="sacl-status-chip"
                                :class="`sacl-status-chip--${
                                    row.status || 'active'
                                }`"
                                :data-testid="`sa-clients-status-chip-${row.id}`"
                            >
                                {{ clientStatusLabel(row.status) }}
                            </span>
                        </td>
                        <td>{{ storeCount(row) }}</td>
                        <td>{{ formatDate(row.last_activity_at) }}</td>
                        <td class="sa-table__actions-cell">
                            <!-- Inner flex row keeps the buttons right-
                                 justified with a gap WITHOUT making the <td>
                                 itself a flex container -- a display:flex td
                                 drops out of the table's vertical-align:middle
                                 flow, so buttons float unevenly when sibling
                                 cells (name/date) wrap to two lines. The cell
                                 stays a normal table cell (middle-aligned);
                                 the wrapper owns the horizontal layout. -->
                            <div class="sacl-actions">
                                <!-- Lifecycle toggle: deactivate an active
                                     client (soft-delete, via confirm modal);
                                     reactivate an inactive one (low-risk
                                     restore, no modal). Closes the reactivate
                                     gap (Trello NMpLnD9J) that left inactive
                                     rows a dead end. -->
                                <button
                                    v-if="row.status === 'inactive'"
                                    type="button"
                                    class="sacl-btn"
                                    :data-testid="`sa-clients-reactivate-btn-${row.id}`"
                                    :disabled="reactivatingId === row.id"
                                    @click.stop="doReactivate(row)"
                                >
                                    Reactivate
                                </button>
                                <button
                                    v-else
                                    type="button"
                                    class="sacl-btn sacl-btn--danger"
                                    :data-testid="`sa-clients-deactivate-btn-${row.id}`"
                                    @click.stop="confirmDeactivate(row)"
                                >
                                    Deactivate
                                </button>
                                <!-- Impersonate: "view as" this client on the
                                     Client Portal, parallel to the SA
                                     Distributors "Impersonate" action. Placed
                                     last so it sits at the far right of the row.
                                     Hidden for inactive clients (cannot
                                     impersonate a soft-deleted client).
                                     DISABLED (with a tooltip) when the client
                                     is not impersonatable -- its primary
                                     contact lacks portal.access, so entering
                                     the portal would 403 and log the SA out.
                                     The click handler still catches the
                                     backend 422 as a safety net for stale
                                     summary data.
                                     Card https://trello.com/c/8eq8uc8E. -->
                                <button
                                    v-if="row.status !== 'inactive'"
                                    type="button"
                                    class="sacl-btn"
                                    :data-testid="`sa-clients-open-${row.id}`"
                                    :disabled="openingId === row.id || !row.impersonatable"
                                    :title="row.impersonatable ? '' : 'This client has no active portal account yet'"
                                    @click.stop="doImpersonateClient(row)"
                                >
                                    Impersonate
                                </button>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>

            <!-- Deactivate confirm modal (inline; small surface).
                 Soft-delete: the client is marked inactive rather than
                 removed, so its stores and history survive. -->
            <div
                v-if="pendingDeactivate"
                class="sacl-modal-backdrop"
                data-testid="sa-clients-deactivate-modal"
            >
                <div class="sacl-modal">
                    <h3>Deactivate client?</h3>
                    <p>
                        Deactivate
                        <strong>{{ pendingDeactivate.name }}</strong
                        >? The client is marked inactive and stays in the
                        list (de-emphasized); its stores and history are
                        kept. You can reactivate it later.
                    </p>
                    <div
                        v-if="deactivateError"
                        class="sacl-modal__error"
                        data-testid="sa-clients-deactivate-error"
                    >
                        {{ deactivateError }}
                    </div>
                    <div class="sacl-modal__actions">
                        <button
                            type="button"
                            class="sacl-btn"
                            data-testid="sa-clients-deactivate-cancel"
                            @click="cancelDeactivate"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            class="sacl-btn sacl-btn--danger"
                            data-testid="sa-clients-deactivate-confirm"
                            :disabled="deactivateBusy"
                            @click="doDeactivate"
                        >
                            Deactivate
                        </button>
                    </div>
                </div>
            </div>

            <table-pagination
                v-if="!loading && !error && count > 0"
                :page="page"
                :rows-per-page="rowsPerPage"
                :total-items="count"
                :rows-per-page-options="[25, 50, 100]"
                @update:page="onPageChange"
                @update:rows-per-page="onRowsPerPageChange"
            />
        </div>
    </v-container>
</template>

<script>
import DistributorLink from "@/components/superadmin/DistributorLink.vue";
import { describeError } from "@/utils/safeErrorLog";
import TablePagination from "@/components/TablePagination.vue";
import ClientsFilterChips from "@/pages/distributor/clients/ClientsFilterChips.vue";
import { emitSidebarRefresh } from "@/stores/sidebarRefresh";
import { useImpersonationStore } from "@/stores/impersonation";

// Client-side "active vs quiet" activity window: 30 days, in ms. Mirrors
// the distributor workspace store's ACTIVITY_WINDOW_MS so the chip
// semantics match what an SA sees on the per-tenant surface.
const ACTIVITY_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

// Table columns, using the SA sortable-table {key,label} idiom. The
// key is BOTH the payload field the column sorts on and the per-column
// data-testid suffix (sa-col-${key}).
const COLUMNS = [
    { key: "name", label: "Name" },
    { key: "distributor_name", label: "Distributor" },
    { key: "email", label: "Email" },
    // Invitation = invitation lifecycle (never_invited / invited /
    // accepted / ...). Its own visible column; ALSO drives the attention
    // chips.
    { key: "invitation_status", label: "Invitation" },
    // Status = client lifecycle status (active/inactive/lead) -- the
    // soft-delete state. Distinct from the invitation column above.
    { key: "status", label: "Status" },
    { key: "store_count", label: "Stores" },
    { key: "last_activity_at", label: "Last activity" },
];

// Readable copy for each invitation-status code (see
// _derive_invitation_status in clients_workspace.py). An unknown code
// renders title-cased rather than silently blank -- a loud fallback that
// flags a new backend code during dev.
const INVITATION_STATUS_LABELS = {
    never_invited: "Never invited",
    invited: "Invited",
    expired: "Expired",
    accepted: "Accepted",
    revoked: "Revoked",
};

function invitationStatusLabel(code) {
    if (!code) return "--";
    if (INVITATION_STATUS_LABELS[code]) return INVITATION_STATUS_LABELS[code];
    // Loud fallback: turn "some_new_code" into "Some new code".
    return String(code)
        .replace(/_/g, " ")
        .replace(/^\w/, (ch) => ch.toUpperCase());
}

// Readable copy for each client lifecycle status. The soft-delete
// affordance moves a client to ``inactive``; ``lead`` / ``active`` are
// the other two states enforced by the Client.status validator. An
// unknown code renders title-cased rather than silently blank.
const CLIENT_STATUS_LABELS = {
    lead: "Lead",
    active: "Active",
    inactive: "Inactive",
};

function clientStatusLabel(code) {
    if (!code) return CLIENT_STATUS_LABELS.active;
    if (CLIENT_STATUS_LABELS[code]) return CLIENT_STATUS_LABELS[code];
    // Loud fallback: turn "some_new_code" into "Some new code".
    return String(code)
        .replace(/_/g, " ")
        .replace(/^\w/, (ch) => ch.toUpperCase());
}

function formatDate(iso) {
    // Render YYYY-MM-DD only -- time of day adds noise. Null = no
    // activity. Matches the SA distributor list's formatDate.
    if (!iso) return "--";
    return String(iso).slice(0, 10);
}

export default {
    name: "SuperAdminClientsList",
    components: { DistributorLink,
        "table-pagination": TablePagination,
        ClientsFilterChips,
    },
    setup() {
        const impersonationStore = useImpersonationStore();
        return { impersonationStore };
    },
    data() {
        return {
            columns: COLUMNS,
            clients: [],
            count: 0,
            loading: false,
            error: "",
            // Server-side filters -> endpoint query params.
            distributorFilter: null,
            storeFilter: null,
            search: "",
            searchInput: "",
            // Pagination.
            page: 1,
            rowsPerPage: 50,
            // Client-side attention chip (narrows the fetched page only).
            chipFilter: "all",
            // Distinct distributors seen so far, for the dropdown.
            distributorOptions: [],
            // Client-side table sort. Default: name ascending. Two clicks
            // on the same column toggle asc -> desc -> asc.
            sortKey: "name",
            sortDir: "asc",
            _searchTimer: null,
            // Soft-delete (deactivate) confirm state.
            pendingDeactivate: null,
            deactivateBusy: false,
            deactivateError: "",
            // Id of the row currently being reactivated (disables its
            // button to prevent a double-click double-PATCH).
            reactivatingId: null,
            // Id of the row whose Open (impersonate) action is in flight
            // (disables its button to prevent a double-start).
            openingId: null,
        };
    },
    computed: {
        // Per-chip counts over the fetched page, so chip labels render
        // "Issues (3)" without forcing a click to discover.
        filterCounts() {
            const counts = {
                issues: 0,
                onboarding: 0,
                active: 0,
                inactive: 0,
                quiet: 0,
                all: this.clients.length,
            };
            for (const c of this.clients) {
                for (const chip of [
                    "issues",
                    "onboarding",
                    "active",
                    "inactive",
                    "quiet",
                ]) {
                    if (this.matchesChip(c, chip)) counts[chip] += 1;
                }
            }
            return counts;
        },
        visibleClients() {
            if (this.chipFilter === "all") return this.clients;
            return this.clients.filter((c) =>
                this.matchesChip(c, this.chipFilter)
            );
        },
        // The chip-narrowed page, sorted by the active column. Sorting is
        // applied on top of the chip filter so the two controls compose.
        sortedClients() {
            const rows = [...this.visibleClients];
            const key = this.sortKey;
            const dir = this.sortDir === "asc" ? 1 : -1;
            rows.sort((a, b) => {
                const av = this._sortVal(a[key]);
                const bv = this._sortVal(b[key]);
                if (av < bv) return -1 * dir;
                if (av > bv) return 1 * dir;
                return 0;
            });
            return rows;
        },
    },
    mounted() {
        if (this.$store && this.$store.state) {
            this.$store.state.title = "Clients";
            this.$store.state.topTitle = false;
        }
        this.fetchClients();
    },
    beforeUnmount() {
        if (this._searchTimer) clearTimeout(this._searchTimer);
    },
    methods: {
        // The row opens the SA client details page (/admin/clients/:id),
        // which reuses the distributor Client workspace inside the SA
        // layout. Never a /distributor route: that layout has no /admin
        // escape (card https://trello.com/c/0SeIzpYd). The row actions use
        // @click.stop so they never also open the page.
        openClient(row) {
            if (!row || row.id == null) return;
            this.$router.push(`/admin/clients/${row.id}`);
        },
        clientStatusLabel,
        invitationStatusLabel,
        formatDate,
        storeCount(row) {
            // The rollup always carries a numeric store_count; render 0 as
            // "0" (a real count), falling back to "--" only if the field is
            // genuinely absent.
            return typeof row.store_count === "number"
                ? row.store_count
                : "--";
        },
        confirmDeactivate(row) {
            this.deactivateError = "";
            this.pendingDeactivate = row;
        },
        cancelDeactivate() {
            this.pendingDeactivate = null;
            this.deactivateError = "";
        },
        async doDeactivate() {
            const row = this.pendingDeactivate;
            if (!row) return;
            this.deactivateBusy = true;
            this.deactivateError = "";
            try {
                // Soft-delete: PATCH status -> inactive through the SAFRS
                // Client surface (JSON:API envelope). The SA holds
                // client.update, so this persists cross-tenant.
                await this.$axios.patch(`/api/clients/${row.id}`, {
                    data: {
                        type: "Client",
                        id: String(row.id),
                        attributes: { status: "inactive" },
                    },
                });
                // Reflect the new status in place so the row stays visible,
                // de-emphasized, without a full refetch.
                row.status = "inactive";
                this.pendingDeactivate = null;
                // Deactivating moves the SA sidebar CLIENTS count -> refresh
                // it. Emit only here, after the await resolves (a failed PATCH
                // throws to the catch and never reaches this line). Generic
                // delegated seam (https://trello.com/c/g9K52FsA).
                //
                // Note: 'clients' is a SHARED summary key -- it is owned by BOTH
                // the SA sidebar (SUPERADMIN_KEYS) and the distributor sidebar
                // (DISTRIBUTOR_KEYS). This true-SA emit carries tenantId:null
                // (effectiveTenantId with no impersonation), which the
                // distributor stale-tenant guard does NOT drop; the two shells
                // never co-mount (route-exclusive) so only the SA store reacts.
                emitSidebarRefresh("clients");
            } catch (err) {
                this.deactivateError =
                    (err && err.message) || "Failed to deactivate client.";
            } finally {
                this.deactivateBusy = false;
            }
        },
        async doReactivate(row) {
            if (!row || this.reactivatingId === row.id) return;
            this.reactivatingId = row.id;
            try {
                // Reactivate is low-risk (restores an inactive client to
                // active), so it skips a confirm modal -- one click ->
                // PATCH status='active' through the same client.update-
                // gated SAFRS surface used by deactivate. Reflect the new
                // status in place so the row loses its inactive styling
                // and its action toggles back to Deactivate, without a
                // full refetch.
                await this.$axios.patch(`/api/clients/${row.id}`, {
                    data: {
                        type: "Client",
                        id: String(row.id),
                        attributes: { status: "active" },
                    },
                });
                row.status = "active";
                // Reactivating moves the SA sidebar CLIENTS count -> refresh
                // it (same generic seam as deactivate). Success-only: a failed
                // PATCH throws to the catch. https://trello.com/c/g9K52FsA.
                emitSidebarRefresh("clients");
            } catch (err) {
                // Surface the failure in the page-level error banner; the
                // row stays inactive so the SA can retry.
                this.error =
                    (err && err.message) || "Failed to reactivate client.";
            } finally {
                this.reactivatingId = null;
            }
        },
        // Normalize sort comparands: null/undefined sort low (empty
        // string) so a missing name / activity does not throw off the
        // comparator -- the SA sortable-table _sortVal idiom. String
        // comparands are lower-cased so "acme" and "Acme" sort together.
        _sortVal(v) {
            if (v === null || v === undefined) return "";
            if (typeof v === "string") return v.toLowerCase();
            return v;
        },
        toggleSort(key) {
            if (this.sortKey === key) {
                this.sortDir = this.sortDir === "asc" ? "desc" : "asc";
            } else {
                this.sortKey = key;
                this.sortDir = "asc";
            }
        },
        buildParams() {
            const params = {
                limit: this.rowsPerPage,
                offset: (this.page - 1) * this.rowsPerPage,
            };
            if (this.distributorFilter != null) {
                params.distributor_id = this.distributorFilter;
            }
            if (this.storeFilter != null) {
                params.store_id = this.storeFilter;
            }
            if (this.search) {
                params.search = this.search;
            }
            return params;
        },
        async fetchClients() {
            this.loading = true;
            this.error = "";
            try {
                const res = await this.$axios.get(
                    "/api/clients/superadmin-summary",
                    { params: this.buildParams() }
                );
                this.clients = res.data.clients || [];
                this.count = Number(res.data.count || 0);
                this.refreshDistributorOptions();
            } catch (err) {
                this.clients = [];
                this.count = 0;
                this.error =
                    (err && err.message) || "Failed to load clients.";
                // eslint-disable-next-line no-console
                console.error(describeError(err));
            } finally {
                this.loading = false;
            }
        },
        refreshDistributorOptions() {
            // Build the dropdown from the distinct distributors present in
            // the response, merged with any previously-seen ones so the
            // currently-selected distributor never vanishes from the list
            // after it filters the page down to its own clients.
            const merged = new Map(
                this.distributorOptions.map((d) => [d.id, d.name])
            );
            for (const c of this.clients) {
                if (c.distributor_id != null && c.distributor_name) {
                    merged.set(c.distributor_id, c.distributor_name);
                }
            }
            this.distributorOptions = Array.from(merged.entries())
                .map(([id, name]) => ({ id, name }))
                .sort((a, b) => String(a.name).localeCompare(String(b.name)));
        },
        onServerFilterChange() {
            this.page = 1;
            this.fetchClients();
        },
        onSearchInput() {
            // 250ms debounce, matching DistributorList -- refetch once the
            // user pauses typing.
            if (this._searchTimer) clearTimeout(this._searchTimer);
            this._searchTimer = setTimeout(() => {
                this.search = this.searchInput;
                this.page = 1;
                this.fetchClients();
            }, 250);
        },
        onChipChange(chip) {
            // Attention chips narrow the already-fetched page client-side;
            // no refetch.
            this.chipFilter = chip;
        },
        onPageChange(p) {
            this.page = p;
            this.fetchClients();
        },
        onRowsPerPageChange(val) {
            this.rowsPerPage = val;
            this.page = 1;
            this.fetchClients();
        },
        // Impersonate this client -- "view as" it on the Client Portal,
        // parallel to the SA Distributors "Impersonate" action. On success
        // we hard-navigate to /portal (NOT a plain reload -- the SA is on
        // /admin/clients and must move to the portal surface). A rejected
        // start (e.g. 422 for a client with no active primary contact)
        // surfaces in the page-level error banner; local impersonation state
        // is untouched by a failed start, so no phantom banner appears.
        // Card https://trello.com/c/8eq8uc8E.
        async doImpersonateClient(row) {
            if (!row || this.openingId === row.id) return;
            this.openingId = row.id;
            try {
                await this.impersonationStore.startClientImpersonation(
                    row.id,
                    row.name
                );
                if (typeof window !== "undefined" && window.location) {
                    window.location.assign("/portal");
                }
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error("Client impersonation failed:", describeError(err));
                this.error =
                    (err &&
                        err.response &&
                        err.response.data &&
                        err.response.data.message) ||
                    (err && err.message) ||
                    "Failed to open client.";
            } finally {
                this.openingId = null;
            }
        },
        // Attention chip predicate. Mirrors the distributor workspace
        // store's matchesFilter so the chip semantics match the per-tenant
        // surface. Kept local (not imported) because the store predicate
        // is module-private; the logic is small and documented here.
        matchesChip(row, chip) {
            const signals = row.attention_signals || [];
            const invStatus = row.invitation_status;
            if (chip === "all") return true;
            if (chip === "issues") return signals.length > 0;
            if (chip === "onboarding") {
                return invStatus === "invited" || invStatus === "expired";
            }
            // "inactive" keys on the LIFECYCLE status field (the soft-delete
            // state), NOT the activity window -- distinct from the
            // "active"/"quiet" activity chips below. Mirrors the distributor
            // workspace store's matchesFilter.
            if (chip === "inactive") return row.status === "inactive";
            if (chip === "active" || chip === "quiet") {
                if (signals.length > 0) return false;
                const at = row.last_activity_at
                    ? Date.parse(row.last_activity_at)
                    : null;
                if (at == null || Number.isNaN(at)) {
                    return chip === "quiet";
                }
                const fresh = Date.now() - at < ACTIVITY_WINDOW_MS;
                return chip === "active" ? fresh : !fresh;
            }
            return true;
        },
    },
};
</script>

<style scoped>
.sacl-page {
    padding: 8px;
}
.sacl-page__header {
    margin-bottom: 12px;
}
.sacl-page__title {
    font-size: 1.25rem;
    font-weight: 600;
    margin: 0;
    color: var(--tf-fg-1);
}
.sacl-page__subtitle {
    margin: 4px 0 0;
    font-size: 13px;
    color: var(--tf-fg-3);
}
.sacl-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 16px;
    flex-wrap: wrap;
}
.sacl-toolbar__server-filters {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}
.sacl-select {
    padding: 6px 10px;
    border: 1px solid var(--tf-border);
    border-radius: var(--tf-radius-xs, 4px);
    background: var(--tf-bg);
    color: var(--tf-fg-1);
    font-size: 13px;
    min-width: 200px;
}
.sacl-search {
    padding: 6px 10px;
    border: 1px solid var(--tf-border);
    border-radius: var(--tf-radius-xs, 4px);
    background: var(--tf-bg);
    color: var(--tf-fg-1);
    font-size: 13px;
    min-width: 280px;
}
.sacl-state {
    padding: 24px;
    text-align: center;
    background: var(--tf-bg);
    border: 1px dashed var(--tf-border);
    border-radius: var(--tf-radius-xs, 4px);
    color: var(--tf-fg-2);
}
.sacl-state--loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
}
.sacl-state--error {
    color: #c0392b;
    border-color: #c0392b;
}
/* Sortable results table -- uses the shared SA .sa-table styling so
   the SA lists read as one surface. Tokenized to the page's existing
   design variables (--tf-*) with the distributor list's neutral fallbacks. */
.sa-table {
    width: 100%;
    border-collapse: collapse;
    background: var(--tf-bg, #fff);
    border: 1px solid var(--tf-border, #e2e8f0);
    border-radius: var(--tf-radius-xs, 6px);
    overflow: hidden;
    margin-bottom: 16px;
}
.sa-table__header {
    text-align: left;
    background: var(--tf-surface-alt, #f7fafc);
    color: var(--tf-fg-1, #2d3748);
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 10px 12px;
    cursor: pointer;
    user-select: none;
    border-bottom: 1px solid var(--tf-border, #e2e8f0);
}
.sa-table__header:hover {
    background: var(--tf-surface-alt, #edf2f7);
}
.sa-table__sort-indicator {
    margin-left: 4px;
    color: var(--tf-fg-2, #4a5568);
}
.sa-table__row td {
    padding: 10px 12px;
    border-bottom: 1px solid var(--tf-border, #edf2f7);
    font-size: 14px;
    color: var(--tf-fg-1, #1a202c);
}
.sa-table__row--link {
    cursor: pointer;
}
.sa-table__row--link:hover td,
.sa-table__row--link:focus-visible td {
    background: var(--tf-surface-alt, #f7fafc);
}
.sa-table__row--link:focus-visible {
    outline: 2px solid var(--tf-accent, #2c5fff);
    outline-offset: -2px;
}
.sa-table__row:last-child td {
    border-bottom: none;
}
/* Soft-deleted (inactive) rows stay visible but de-emphasized. */
.sa-table__row--inactive td {
    color: var(--tf-fg-3, #94a3b8);
    background: var(--tf-surface-alt, #f8fafc);
}
.sa-table__header--actions {
    cursor: default;
    text-align: right;
}
.sa-table__actions-cell {
    /* Stays a normal table cell: right-aligned, vertically centered like
       every other cell in the row. The buttons' horizontal layout lives on
       the inner .sacl-actions wrapper -- putting display:flex on the <td>
       itself drops it out of the table's vertical-align flow and made the
       buttons float unevenly when sibling cells wrapped to two lines. */
    text-align: right;
    vertical-align: middle;
    white-space: nowrap;
}
/* Inner flex row for the action buttons: right-justified with a small,
   consistent gap (e.g. Deactivate | Impersonate) without disturbing the
   parent cell's table-alignment. */
.sacl-actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
}
/* Invitation status -- a plain neutral label (distinct from the
   lifecycle status chip so the two columns read as separate signals). */
.sacl-invitation-chip {
    color: var(--tf-fg-2, #4a5568);
    font-size: 13px;
}
/* Client lifecycle status chip -- mirrors the distributor list chips so
   the two surfaces read as one design system. */
.sacl-status-chip {
    display: inline-block;
    padding: 2px 8px;
    font-size: 12px;
    border-radius: 12px;
    border: 1px solid var(--tf-border, #e2e8f0);
    background: var(--tf-bg, #fff);
}
.sacl-status-chip--lead {
    background: #fff4e0;
    border-color: #f0b96b;
    color: #6b4500;
}
.sacl-status-chip--active {
    background: #e6f7e6;
    border-color: #6cbf6c;
    color: #14571c;
}
.sacl-status-chip--inactive {
    background: var(--tf-surface-alt, #f1f5f9);
    color: var(--tf-fg-3, #64748b);
}
.sacl-btn {
    padding: 4px 10px;
    border: 1px solid var(--tf-border-strong, #cbd5e1);
    border-radius: var(--tf-radius-xs, 4px);
    background: var(--tf-bg, #fff);
    color: var(--tf-fg-1, #1a202c);
    font-size: 13px;
    cursor: pointer;
}
.sacl-btn:hover {
    background: var(--tf-surface-alt, #f7fafc);
}
.sacl-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
.sacl-btn--danger {
    color: #c0392b;
    border-color: #e2b6b0;
}
.sacl-modal .sacl-btn--danger {
    color: #fff;
    background: #c0392b;
    border-color: #c0392b;
}
.sacl-modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}
.sacl-modal {
    background: var(--tf-bg, #fff);
    padding: 24px;
    border-radius: var(--tf-radius-sm, 6px);
    max-width: 420px;
    width: 90%;
    color: var(--tf-fg-1, #1a202c);
}
.sacl-modal h3 {
    margin: 0 0 12px;
}
.sacl-modal__error {
    margin-top: 12px;
    color: #c0392b;
    font-size: 13px;
}
.sacl-modal__actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 16px;
}
</style>
